import { useState, useEffect, useRef } from 'react';
import { X, UserCircle } from 'lucide-react';
import { apiService } from '../../services';

// Fallback questions if no role-specific questions are available
const FALLBACK_QUESTIONS = [
  {
    category: "General",
    questions: [
      "Show me all events from the last 24 hours",
      "What are the most common event types?",
      "Show me events with errors",
    ]
  },
  {
    category: "Analytics",
    questions: [
      "What is the average processing time?",
      "Show me the busiest locations",
      "Which products have the most events?",
    ]
  },
  {
    category: "Tracking",
    questions: [
      "Where is product XYZ right now?",
      "Show me the journey of container ABC",
      "List all shipments in transit",
    ]
  }
];

const QuestionSuggestions = ({ isOpen, onClose, onSelectQuestion }) => {
  const [questions, setQuestions] = useState(FALLBACK_QUESTIONS);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const containerRef = useRef(null);
  const selectedQuestionRef = useRef(null);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(localStorage.getItem('selectedRoleUuid') || '');
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  // Fetch roles from API
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsLoadingRoles(true);
        const rolesData = await apiService.getRoles();
        const rolesList = rolesData?.roles || [];
        setRoles(rolesList);

        // If we have roles and no role is selected, select the first one
        if (rolesList.length > 0 && !selectedRole) {
          const firstRole = rolesList[0];
          setSelectedRole(firstRole.uuid);
          localStorage.setItem('selectedRoleUuid', firstRole.uuid);
          localStorage.setItem('selectedRoleName', firstRole.role_name);

          // Fetch and save detailed role information
          try {
            const roleDetails = await apiService.getRoleDetails(firstRole.uuid);
            if (roleDetails) {
              localStorage.setItem('roleDetails', JSON.stringify(roleDetails));
            }
          } catch (detailsError) {
            // Failed to fetch role details silently
          }
        }
      } catch (error) {
        // Failed to load roles silently
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchRoles();
  }, [selectedRole]);

  // Handle role selection
  const handleRoleChange = async (e) => {
    const uuid = e.target.value;
    setSelectedRole(uuid);

    const selectedRoleObj = roles.find(role => role.uuid === uuid);
    if (selectedRoleObj) {
      localStorage.setItem('selectedRoleUuid', uuid);
      localStorage.setItem('selectedRoleName', selectedRoleObj.role_name);

      try {
        const roleDetails = await apiService.getRoleDetails(uuid);
        if (roleDetails) {
          localStorage.setItem('roleDetails', JSON.stringify(roleDetails));

          // Immediately update questions with the new role details
          if (roleDetails.roles && roleDetails.roles.questions && Array.isArray(roleDetails.roles.questions)) {
            const roleName = roleDetails.roles.role_name || selectedRoleObj.role_name;
            const roleQuestions = roleDetails.roles.questions;

            setQuestions([{
              category: roleName,
              questions: roleQuestions
            }]);
            setSelectedCategoryIndex(0);
            setSelectedQuestionIndex(0);
          }
        }
      } catch (error) {
        // Failed to fetch role details silently
      }
    }
  };

  // Load role-specific questions from localStorage when role changes
  useEffect(() => {
    try {
      // Get role details from localStorage
      const roleDetailsString = localStorage.getItem('roleDetails');

      if (roleDetailsString) {
        const roleDetailsData = JSON.parse(roleDetailsString);

        // Handle the specific structure where questions are in the roles object
        if (roleDetailsData.roles && roleDetailsData.roles.questions && Array.isArray(roleDetailsData.roles.questions)) {
          const roleName = roleDetailsData.roles.role_name || localStorage.getItem('selectedRoleName') || 'Role-Specific';
          const roleQuestions = roleDetailsData.roles.questions;

          const formattedQuestions = [{
            category: roleName,
            questions: roleQuestions
          }];

          setQuestions(formattedQuestions);
          // Reset selection when questions change
          setSelectedCategoryIndex(0);
          setSelectedQuestionIndex(0);
          return;
        }

        // Check if role details contain questions directly
        if (roleDetailsData.questions && Array.isArray(roleDetailsData.questions)) {
          // Format questions into categories if they're not already
          let formattedQuestions;

          // If questions is just an array of strings, create a single category
          if (typeof roleDetailsData.questions[0] === 'string') {
            const roleName = roleDetailsData.role_name || localStorage.getItem('selectedRoleName') || 'Role-Specific';
            formattedQuestions = [{
              category: roleName,
              questions: roleDetailsData.questions
            }];
          }
          // If questions is already an array of category objects
          else if (roleDetailsData.questions[0] && roleDetailsData.questions[0].category) {
            formattedQuestions = roleDetailsData.questions;
          }
          // Any other format, create a default category
          else {
            formattedQuestions = [{
              category: 'Suggested Questions',
              questions: roleDetailsData.questions.map(q =>
                typeof q === 'string' ? q : (q.text || JSON.stringify(q))
              )
            }];
          }

          setQuestions(formattedQuestions);
          // Reset selection when questions change
          setSelectedCategoryIndex(0);
          setSelectedQuestionIndex(0);
          return;
        }
      }

      // If no valid role questions found, use fallback
      setQuestions(FALLBACK_QUESTIONS);
      // Reset selection when questions change
      setSelectedCategoryIndex(0);
      setSelectedQuestionIndex(0);
    } catch (error) {
      setQuestions(FALLBACK_QUESTIONS);
      // Reset selection when questions change
      setSelectedCategoryIndex(0);
      setSelectedQuestionIndex(0);
    }
  }, [selectedRole]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (selectedQuestionIndex > 0) {
            // Move up within the same category
            setSelectedQuestionIndex(selectedQuestionIndex - 1);
          } else if (selectedCategoryIndex > 0) {
            // Move to the last question of the previous category
            const prevCategoryIndex = selectedCategoryIndex - 1;
            const prevCategoryQuestionsCount = questions[prevCategoryIndex].questions.length;
            setSelectedCategoryIndex(prevCategoryIndex);
            setSelectedQuestionIndex(prevCategoryQuestionsCount - 1);
          }
          break;
          
        case 'ArrowDown':
          e.preventDefault();
          if (selectedQuestionIndex < questions[selectedCategoryIndex].questions.length - 1) {
            // Move down within the same category
            setSelectedQuestionIndex(selectedQuestionIndex + 1);
          } else if (selectedCategoryIndex < questions.length - 1) {
            // Move to the first question of the next category
            setSelectedCategoryIndex(selectedCategoryIndex + 1);
            setSelectedQuestionIndex(0);
          }
          break;
          
        case 'Enter':
          e.preventDefault();
          const selectedQuestion = questions[selectedCategoryIndex].questions[selectedQuestionIndex];
          onSelectQuestion(selectedQuestion);
          onClose();
          
          // Dispatch a custom event to ensure input focus - with a small delay
          setTimeout(() => {
            try {
              window.dispatchEvent(new CustomEvent('focusChatInput'));
            } catch (err) {
              // Failed silently
            }
          }, 50);
          break;
          
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
          
        default:
          break;
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, questions, selectedCategoryIndex, selectedQuestionIndex, onSelectQuestion, onClose]);

  // Scroll selected question into view
  useEffect(() => {
    if (selectedQuestionRef.current) {
      selectedQuestionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedCategoryIndex, selectedQuestionIndex]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  return (
    <div
      ref={containerRef}
      className={`fixed right-0 top-0 bottom-20 w-80 bg-gray-100 border-l border-gray-300 shadow-lg transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      <div className="flex flex-col h-full">
        <div className="p-6 border-b border-gray-300 bg-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            {localStorage.getItem('selectedRoleName')
              ? `${localStorage.getItem('selectedRoleName')} Questions`
              : 'Suggested Questions'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-300 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Role Selection */}
        {roles.length > 0 && (
          <div className="p-4 border-b border-gray-300 bg-gray-100">
            <div className="relative">
              <select
                value={selectedRole}
                onChange={handleRoleChange}
                className="w-full py-2 px-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-gray-500"
                disabled={isLoadingRoles}
              >
                {roles.map((role, index) => (
                  <option key={`${role.uuid}-${index}`} value={role.uuid}>
                    {role.role_name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <UserCircle className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {questions.length > 0 ? (
            questions.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 px-2">
                  {category.category}
                </h3>
                <div className="space-y-1.5">
                  {category.questions.map((question, questionIndex) => {
                    const isSelected = categoryIndex === selectedCategoryIndex && questionIndex === selectedQuestionIndex;
                    return (
                      <button
                        key={questionIndex}
                        ref={isSelected ? selectedQuestionRef : null}
                        onClick={() => {
                          onSelectQuestion(question);
                          onClose();

                          // Dispatch a custom event to ensure input focus - with a small delay
                          setTimeout(() => {
                            try {
                              window.dispatchEvent(new CustomEvent('focusChatInput'));
                            } catch (err) {
                              // Failed silently
                            }
                          }, 50);
                        }}
                        onMouseEnter={() => {
                          setSelectedCategoryIndex(categoryIndex);
                          setSelectedQuestionIndex(questionIndex);
                        }}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors border ${
                          isSelected
                            ? 'bg-gray-600 text-white shadow-sm border-transparent'
                            : 'bg-white hover:bg-gray-100 text-gray-700 border-gray-200'
                        }`}
                      >
                        {question}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 mt-8">
              No questions available.
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-300 bg-gray-100">
          <p className="text-xs text-gray-600 text-center">
            Use ↑↓ arrows to navigate, Enter to select
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuestionSuggestions; 